import './ChatModal.css'

import { useEffect, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import { agentChat, websocket } from '../store'
import { useRheyaVoice } from '../hooks/useRheyaVoice'
import { rheyaStatusLabel } from '../features/rheya/messageList'
import { playMessageVoice } from '../lib/voice'

import rheyaAvatar from '../assets/rheya-avatar.jpg'
import { Chat } from './Chat'
import { ChatBubble } from './ChatBubble'
import { ToolBubble } from './ToolBubble'

export type ChatModalProps = {
  initialContext?: string
  onClose: () => void
  onMinimize: () => void
}

export const ChatModal = (props: ChatModalProps) => {
  const { initialContext, onClose, onMinimize } = props

  const [inputValue, setInputValue] = useState('')
  const connectionStatus = useAtomValue(websocket.connectionStatusAtom)
  const messages = useAtomValue(agentChat.agentChatMessagesAtom)
  const isLoading = useAtomValue(agentChat.agentChatIsLoadingAtom)
  const isComplete = useAtomValue(agentChat.agentChatIsCompleteAtom)
  const error = useAtomValue(agentChat.agentChatErrorAtom)
  const status = useAtomValue(agentChat.agentChatStatusAtom)
  const chatMode = useAtomValue(agentChat.agentChatModeAtom)
  const streamHeldIds = useAtomValue(agentChat.rheyaVoiceHeldMessageIdsAtom)
  const connect = useSetAtom(agentChat.agentChatConnectAtom)
  const sendMessage = useSetAtom(agentChat.agentChatSendMessageAtom)
  const disconnect = useSetAtom(agentChat.agentChatDisconnectAtom)
  const playRemote = useSetAtom(agentChat.agentChatPlayMessageAtom)
  const setRemoteVoice = useSetAtom(agentChat.agentChatSetVoiceAtom)
  const voiceStopRemote = useSetAtom(agentChat.agentChatVoiceStopAtom)

  const voice = useRheyaVoice()

  // Connect while the socket is up; do not stop TTS on reconnect blips.
  useEffect(() => {
    if (connectionStatus !== 'connected') return
    connect({ mode: chatMode, initialContext })
    return () => {
      disconnect()
    }
  }, [connectionStatus, initialContext, chatMode, connect, disconnect])

  const setHeldIds = useSetAtom(agentChat.rheyaVoiceHeldMessageIdsAtom)

  // Keep server live-TTS flag in sync with mute; unhold bubbles when muted.
  useEffect(() => {
    if (connectionStatus !== 'connected') return
    setRemoteVoice(!voice.muted)
    if (voice.muted) setHeldIds(new Set())
  }, [voice.muted, connectionStatus, setRemoteVoice, setHeldIds])

  // Stop voice only when this modal unmounts (close), not when WS reconnects.
  useEffect(() => {
    return () => {
      voiceStopRemote()
      void voice.stop()
    }
  }, [])

  const deliverVoiceText = (text: string) => {
    const body = text.trim()
    if (!body || isLoading || isComplete) return
    sendMessage(body)
  }

  useEffect(() => {
    voice.setOnAutoSend(deliverVoiceText)
    return () => voice.setOnAutoSend(null)
  }, [isLoading, isComplete])

  const handleSubmit = () => {
    if (!inputValue.trim() || isLoading || isComplete) return
    sendMessage(inputValue.trim())
    setInputValue('')
  }

  const handleClose = () => {
    void voice.stop()
    onClose()
  }

  const isProfile = chatMode === 'profile'
  const heldKeys = new Set([...voice.heldKeys, ...streamHeldIds])
  const softStatus = rheyaStatusLabel(status, heldKeys.size)

  return (
    <div className={`chat-modal${isProfile ? ' chat-modal--profile' : ''}`}>
      <header className="chat-modal__header">
        <div className="chat-modal__header-info">
          <img className="chat-modal__avatar" src={rheyaAvatar} alt="Rheya" />
          <div>
            <h2 className="chat-modal__title">{isProfile ? 'Edit profile' : 'Rheya'}</h2>
            <p className="chat-modal__subtitle">
              {isProfile ? 'with Rheya · updates your profile' : 'Your matchmaker'}
            </p>
          </div>
        </div>
        <div className="chat-modal__header-actions">
          <button
            type="button"
            className="chat-modal__mute"
            onClick={voice.toggleMute}
            aria-label={voice.muted ? 'Unmute Rheya' : 'Mute Rheya'}
            title={voice.muted ? 'Unmute' : 'Mute'}
          >
            {voice.muted ? 'Muted' : voice.speaking ? 'Speaking…' : 'Voice'}
          </button>
          {!isProfile ? (
            <button
              type="button"
              className="chat-modal__minimize"
              onClick={onMinimize}
              aria-label="Minimize"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14" />
              </svg>
            </button>
          ) : null}
          <button
            type="button"
            className="chat-modal__close"
            onClick={handleClose}
            aria-label={isProfile ? 'Done editing profile' : 'Close'}
          >
            {isProfile ? (
              <span className="chat-modal__done">Done</span>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {isProfile ? (
        <p className="chat-modal__profile-hint">
          You&apos;re still on your profile. Describe a change and Rheya will update what matches
          see.
        </p>
      ) : null}

      <div className="chat-modal__body">
        {error || voice.voiceError ? (
          <div className="chat-modal__error">
            <p>{error || voice.voiceError}</p>
          </div>
        ) : messages.length === 0 && isLoading ? (
          <div className="chat-modal__loading">
            {isProfile ? 'Opening profile editor…' : 'Connecting to Rheya...'}
          </div>
        ) : (
          <Chat
            messages={messages}
            inputValue={inputValue}
            inputPlaceholder={isProfile ? 'Describe a profile change…' : 'Type a message...'}
            disabled={isLoading || isComplete}
            heldKeys={heldKeys}
            playingKey={voice.playingKey}
            onPlayMessage={(text, key) => {
              void playMessageVoice(key, () => playRemote(key), text).catch((err: unknown) => {
                console.warn('[ChatModal] play failed', err)
              })
            }}
            indicator={
              status.type === 'toolUse' ? (
                <ToolBubble toolUse={status.toolUse} active />
              ) : softStatus ? (
                <ChatBubble sender="status">{softStatus}</ChatBubble>
              ) : undefined
            }
            onInputChange={setInputValue}
            onSubmit={handleSubmit}
            showMic
            recording={voice.recording}
            micBusy={voice.voiceBusy}
            levels={voice.levels}
            onMicPress={() => {
              void voice.startRecording()
            }}
            onCancelRecording={() => {
              void voice.cancelRecording()
            }}
            onSendRecording={() => {
              void (async () => {
                const text = await voice.sendRecording()
                if (text) deliverVoiceText(text)
              })()
            }}
          />
        )}
      </div>
    </div>
  )
}
